import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { Plus, Trash2 } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Breadcrumbs,
  Badge,
  Card,
  CardHeader,
  CardBody,
  DataTable,
  Button,
  Input,
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  CopyToClipboard,
  ConfirmDialog,
  Toggle,
} from '../../../design-system/components';
import {
  admin_campaigns_path,
  admin_campaign_path,
  admin_campaign_invites_path,
  admin_campaign_invite_path,
  toggle_active_admin_campaign_invite_path,
} from '../../../lib/routes';

function inviteStatusBadge(invite) {
  if (!invite.active) return <Badge variant="default" size="sm">Inactive</Badge>;
  if (invite.expired) return <Badge variant="danger" size="sm">Expired</Badge>;
  if (invite.exhausted) return <Badge variant="warning" size="sm">Exhausted</Badge>;
  return <Badge variant="success" size="sm">Active</Badge>;
}

function CreateInviteModal({ open, onClose, campaignId }) {
  const [name, setName] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [processing, setProcessing] = useState(false);

  function handleSubmit(e) {
    e.preventDefault();
    setProcessing(true);
    router.post(
      admin_campaign_invites_path(campaignId),
      {
        invite: {
          name: name || null,
          max_uses: maxUses ? parseInt(maxUses, 10) : null,
          expires_at: expiresAt || null,
        },
      },
      {
        onFinish: () => {
          setProcessing(false);
          setName('');
          setMaxUses('');
          setExpiresAt('');
          onClose();
        },
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <ModalTitle>Create Invite Link</ModalTitle>
        <ModalBody>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-fg mb-1">Name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cold email batch #1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg mb-1">Max Uses (optional)</label>
              <Input
                type="number"
                min="1"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                placeholder="Unlimited"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg mb-1">Expires At (optional)</label>
              <Input
                type="datetime-local"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
              />
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={processing}>
            {processing ? 'Creating...' : 'Create'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

function buildInviteColumns(campaignId) {
  return [
    {
      id: 'name',
      header: 'Name',
      render: (row) => (
        <span className="text-sm text-fg">{row.name || <span className="text-fg-tertiary italic">Unnamed</span>}</span>
      ),
    },
    {
      id: 'link',
      header: 'Invite Link',
      render: (row) => {
        const url = `${window.location.origin}/invite/${row.token}`;
        return (
          <CopyToClipboard text={url}>
            <span className="font-mono text-xs text-fg-tertiary truncate max-w-[200px] inline-block align-bottom">
              /invite/{row.token}
            </span>
          </CopyToClipboard>
        );
      },
    },
    {
      id: 'uses',
      header: 'Uses',
      render: (row) => (
        <span className="text-sm text-fg">
          {row.uses_count}{row.max_uses != null ? ` / ${row.max_uses}` : ''}
        </span>
      ),
    },
    {
      id: 'expires',
      header: 'Expires',
      render: (row) => (
        <span className="text-sm text-fg-secondary">
          {row.expires_at
            ? new Date(row.expires_at).toLocaleDateString()
            : 'Never'}
        </span>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      render: (row) => inviteStatusBadge(row),
    },
    {
      id: 'actions',
      header: '',
      render: (row) => (
        <InviteActions invite={row} campaignId={campaignId} />
      ),
    },
  ];
}

function InviteActions({ invite, campaignId }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  return (
    <div className="flex items-center justify-end gap-2">
      <Toggle
        checked={invite.active}
        onChange={() =>
          router.post(toggle_active_admin_campaign_invite_path(campaignId, invite.id))
        }
      />
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setConfirmDelete(true)}
        title="Delete"
      >
        <Trash2 size={16} className="text-fg-danger" />
      </Button>
      <ConfirmDialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          router.delete(admin_campaign_invite_path(campaignId, invite.id))
        }
        title="Delete Invite Link"
        message="Are you sure you want to delete this invite link? This cannot be undone."
        confirmLabel="Delete"
        destructive
      />
    </div>
  );
}

export default function Links({ campaign, invites }) {
  const [showCreateInvite, setShowCreateInvite] = useState(false);

  const inviteColumns = buildInviteColumns(campaign.id);

  return (
    <AdminLayout>
      <Breadcrumbs
        items={[
          { label: 'Campaigns', href: admin_campaigns_path() },
          { label: campaign.name, href: admin_campaign_path(campaign.id) },
          { label: 'Invite Links' },
        ]}
      />
      <PageHeader
        title="Invite Links"
        description={`Manage invite links for ${campaign.name}`}
        actions={
          <Button size="sm" onClick={() => setShowCreateInvite(true)}>
            <Plus size={16} />
            Create Invite Link
          </Button>
        }
      />

      <div className="mt-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">
              Invite Links ({invites.length})
            </h3>
          </CardHeader>
          <CardBody className="p-0">
            <DataTable
              columns={inviteColumns}
              data={invites}
              keyAccessor="id"
              emptyTitle="No invite links"
              emptyDescription="Create invite links to let people join this campaign directly without applying."
              className="border-0 rounded-none"
            />
          </CardBody>
        </Card>
      </div>

      <CreateInviteModal
        open={showCreateInvite}
        onClose={() => setShowCreateInvite(false)}
        campaignId={campaign.id}
      />
    </AdminLayout>
  );
}
